import React, { useState } from 'react';
import { openCertificate } from '@/lib/certificates';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Award, ExternalLink, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export function RecognizeMentorDialog({ mentor, open, onOpenChange, onSent }) {
  const pending = mentor?.recognition_pending || [];
  const [threshold, setThreshold] = useState(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const selected = pending.find(p => p.threshold === threshold) || pending[0];
  const approved = !!mentor?.principal_cert_approved;

  const send = async () => {
    if (!selected) return;
    setSending(true);
    setError('');
    try {
      const r = await api.post(`/coordinator/mentors/${mentor.id}/recognize`, {
        threshold: selected.threshold,
        message: message.trim(),
      });
      toast.success(`${selected.title} sent to ${r.data.sent_to}`);
      onSent?.();
      onOpenChange(false);
      setMessage('');
    } catch (e) {
      setError(e?.response?.data?.detail || 'The recognition could not be sent. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (!mentor) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-2xl" data-testid="recognize-mentor-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-lg">Recognise {mentor.parent_name}</DialogTitle>
        </DialogHeader>

        {pending.length === 0 ? (
          <p className="text-sm text-[#6B7280]" data-testid="recognize-nothing-pending">
            Every milestone this mentor has reached has already been sent.
          </p>
        ) : (
          <>
            {pending.length > 1 && (
              <div data-testid="recognize-level-picker">
                <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider mb-1">
                  {pending.length} milestones waiting — choose one to send
                </p>
                <div className="flex flex-wrap gap-2">
                  {pending.map(p => {
                    const active = p.threshold === selected?.threshold;
                    return (
                      <button
                        key={p.threshold}
                        type="button"
                        onClick={() => setThreshold(p.threshold)}
                        className="rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors"
                        style={{
                          borderColor: active ? p.color : '#E5E7EB',
                          backgroundColor: active ? `${p.color}14` : '#FFFFFF',
                          color: active ? p.color : '#6B7280',
                        }}
                        data-testid={`recognize-level-${p.threshold}`}
                      >
                        {p.title}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="rounded-xl border p-4 mt-1" style={{ borderColor: `${selected.color}59`, backgroundColor: `${selected.color}0D` }}>
              <div className="flex items-center gap-2">
                <Award className="h-4 w-4" style={{ color: selected.color }} />
                <p className="text-sm font-bold text-[#1F2937] font-['Outfit']" data-testid="recognize-selected-title">{selected.title}</p>
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ color: selected.color, backgroundColor: `${selected.color}1F` }}>
                  {selected.threshold} experiences
                </span>
              </div>
              <p className="text-sm text-[#6B7280] mt-1.5">
                {selected.threshold} completed mentoring experiences · {mentor.impact?.students_reached || 0} students reached
              </p>
              {selected.certificate_url ? (
                <button
                  onClick={() => openCertificate(selected.certificate_url)}
                  className="inline-flex items-center gap-1.5 text-xs font-medium mt-3 hover:underline"
                  style={{ color: selected.color }}
                  data-testid="recognize-preview-link"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> Preview certificate
                </button>
              ) : (
                <p className="text-xs text-amber-600 mt-3" data-testid="recognize-no-certificate">Certificate not ready yet.</p>
              )}
            </div>

            {!approved && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2" data-testid="recognize-approval-warning">
                <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-700 leading-relaxed">
                  Recognition cannot be sent until your school's authorised administrator adds and approves the
                  principal's name in School Settings → Mentor Recognition Certificates.
                </p>
              </div>
            )}

            <div>
              <label className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider">Appreciation message (optional)</label>
              <Textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows={3}
                placeholder="A line the mentor will read in the email, in your words."
                className="mt-1 rounded-lg text-sm border-[#E5E7EB]"
                data-testid="recognize-message-input"
              />
            </div>

            {error && (
              <p className="text-xs text-red-600" data-testid="recognize-error">{error}</p>
            )}
          </>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" className="rounded-lg text-sm border-[#E5E7EB]" onClick={() => onOpenChange(false)} data-testid="recognize-cancel-btn">
            Cancel
          </Button>
          {pending.length > 0 && (
            <Button
              className="rounded-lg text-sm bg-[#F97D6A] hover:bg-[#F06651] text-white"
              disabled={!approved || sending || !selected?.certificate_url}
              onClick={send}
              data-testid="recognize-send-btn"
            >
              {sending ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Sending</> : error ? 'Try again' : 'Send recognition'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
